/**
 * Workflow Editor Component
 * Main component that combines all parts of the workflow editor with API integration
 */

import React, { useEffect } from 'react';
import { ZapIcon, PanelRightIcon, WarningIcon, ChevronRightIcon, ArrowLeftIcon } from '@bizfirst/common-react';
import { ReactFlowProvider } from 'reactflow';
import { useWorkflowStore } from '@flow-studio/store';
import { useUIStore } from '@flow-studio/store';
import { useDesignerModeStore } from '@flow-studio/store';
import { useFlowObserverSignalR, useConsoleLogCapture, FlowFooterEngine } from '@flow-studio/flow-observer-core';
import { FlowObserverPanel } from '@flow-studio/flow-observer-panel';
import { useExecutionSignalR } from '../hooks/useExecutionSignalR';
import { useHilSignalR } from '../hooks/useHilSignalR';
import { useHilPanelBridge } from '../hooks/useHilPanelBridge';
// HIL rendering (HilOverlay/HilChatWindow) is mounted once, at the WorkflowApp root —
// not here. WorkflowEditor only drives HIL *detection* via useHilSignalR below; a
// second render mount here previously duplicated the stateful chat composer for the
// same session (each instance accumulating its own writer/echo state independently),
// which could silently drop steps if a user interacted with the "wrong" stacked
// instance. Removed rather than left duplicated once the risk was identified.
//
// UPDATE: a leftover `<HilOverlay />` floating-panel mount (from @bizfirst/hil-ui) was
// still present here despite this comment already documenting the opposite intent —
// it rendered the exact same active HIL session a second time, on top of
// HilObservePanel's "HIL Actions" dock tab (which already handles every HIL type:
// HilChatWindow for chat, HilPresentationRenderer for form/approval/info), confirmed
// live via E2E testing 2026-08-18. Removed for real this time, along with the
// redundant HilFormRendererProvider wrap — WorkflowApp.tsx already wraps the whole
// app (including this component and FlowObserverPanel) with one, so this second
// nested provider was never necessary.

import { Button } from './Shared/FormUI';
import { Loader } from './Shared/Loader';
import { TopToolbar } from './Toolbar/TopToolbar';
import { LeftToolbar } from './Toolbar/LeftToolbar';
import { RightToolbar } from './Toolbar/RightToolbar';
import { WorkflowCanvas } from './Canvas/WorkflowCanvas';
import { HandleProperties } from './Properties/HandleProperties';
import { EdgeProperties } from './Properties/EdgeProperties';
import './workflowEditor.css';

interface WorkflowEditorProps {
  processThreadId?: number;
  versionId?: number;
  workflowName?: string;
  // For backward compatibility with mock data mode
  workflowId?: string;
  useMockData?: boolean;
}

export const WorkflowEditor: React.FC<WorkflowEditorProps> = ({
  processThreadId,
  versionId,
  workflowName,
  workflowId = 'wf-001',
  useMockData = false,
}) => {
  const {
    loadWorkflow,
    loadWorkflowFromApi,
    workflow,
    nodes,
    edges,
    isLoading,
    loadError,
    setCurrentContext,
  } = useWorkflowStore();
  const { theme, leftPanelOpen, toggleLeftPanel, rightPanelOpen, toggleRightPanel, selectedHandle, selectedEdges } = useUIStore();

  // Three-mode designer
  const mode = useDesignerModeStore((s) => s.mode);
  const currentExecutionId = useDesignerModeStore((s) => s.currentExecutionId);
  const resetToDesignMode = useDesignerModeStore((s) => s.resetToDesignMode);

  // SignalR: connect to execution hub, update node statuses, dispatch 'execution-event' window events
  const { connection } = useExecutionSignalR();

  // HIL SignalR: join hil stream group and drive the HIL overlay
  useHilSignalR(connection, currentExecutionId?.ResID ?? null);

  // Keep the HIL footer badge and dock tab in sync with the real HIL store
  useHilPanelBridge();

  // FlowObserver: consume 'execution-event' window events and push data into the observer panel/footer
  useFlowObserverSignalR();

  // Re-fit the canvas whenever a side panel opens/closes — WorkflowCanvas already listens for
  // this exact event (see its 'flow-studio:fit-view' window listener) for other triggers, so
  // this reuses it rather than adding a second mechanism. Fixes a real bug: at narrower
  // desktop/tablet widths, opening the node palette and/or properties panel can push canvas
  // content (e.g. a second workflow node) out of view with nothing to bring it back into frame
  // until the user manually pans/zooms. The panels' own CSS transition is 0.3s; wait slightly
  // longer so the canvas container has already reached its final size before fitting to it.
  React.useEffect(() => {
    const timer = setTimeout(() => {
      window.dispatchEvent(new Event('flow-studio:fit-view'));
    }, 350);
    return () => clearTimeout(timer);
  }, [leftPanelOpen, rightPanelOpen]);

  // Console Log: when enabled, intercept fetch/xhr and socket events for the Console Log observer tab
  useConsoleLogCapture();

  // Track if we've loaded this workflow to prevent re-loading on every render
  const loadedRef = React.useRef<string | null>(null);

  // Load workflow on mount (only once per workflow)
  useEffect(() => {
    const workflowKey = useMockData ? workflowId : `${processThreadId}-${versionId}`;

    // Only load if we haven't loaded this workflow yet
    if (loadedRef.current === workflowKey) {
      return;
    }

    // Clear any lingering execution state from a previous workflow.
    resetToDesignMode();

    if (useMockData) {
      // Backward compatibility: use mock data
      setCurrentContext(-1, -1); // sentinel — prevents "no context" error, skips real API calls
      loadWorkflow(workflowId);
      loadedRef.current = workflowKey;
    } else if (processThreadId && versionId) {
      // New way: load from API
      setCurrentContext(processThreadId, versionId);
      loadWorkflowFromApi(processThreadId, versionId, workflowName);
      loadedRef.current = workflowKey;
    }
    // Only depend on the workflow identifiers, not the functions
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processThreadId, versionId, workflowId, useMockData]);

  // Keep active-workflow footer badge in sync with the loaded workflow name.
  // Using the extracted scalar avoids flicker: the effect only re-runs when the
  // name itself changes, not when the workflow object gets a new reference (e.g.
  // after a save that returns a fresh object with the same name).
  // Cleanup hides the badge when WorkflowEditor unmounts (user leaves the designer).
  const loadedWorkflowName = workflow?.name;
  useEffect(() => {
    if (loadedWorkflowName) {
      FlowFooterEngine.updateBadge('active-workflow', {
        fallbackLabel: loadedWorkflowName,
        visible: true,
      });
    } else {
      FlowFooterEngine.setBadgeVisibility('active-workflow', false);
    }
    return () => {
      FlowFooterEngine.setBadgeVisibility('active-workflow', false);
    };
  }, [loadedWorkflowName]);

  // Show loading state
  if (isLoading) {
    return (
      <ReactFlowProvider>
        <div className="workflow-editor-container">
          <TopToolbar />
          <div className="workflow-editor-loading">
            <Loader size="lg" label="Loading workflow..." />
          </div>
        </div>
      </ReactFlowProvider>
    );
  }

  // Show error state
  if (loadError) {
    console.error(' [WorkflowEditor] Displaying error screen:', loadError);
    console.error(' [WorkflowEditor] Current state:', {
      processThreadId,
      versionId,
      nodes,
      edges,
      isLoading,
      loadError
    });

    return (
      <ReactFlowProvider>
        <div className="workflow-editor-container">
          <TopToolbar />
          <div className="workflow-editor-error">
            <div className="workflow-editor-error__icon"><WarningIcon size={24} /></div>
            <div className="workflow-editor-error__message">
              Error loading workflow: {loadError}
            </div>
            <Button variant="primary" onClick={() => {
                if (processThreadId && versionId) {
                  loadWorkflowFromApi(processThreadId, versionId);
                } else if (useMockData) {
                  loadWorkflow(workflowId);
                }
              }}>
              <ZapIcon size={14} /> Retry
            </Button>
          </div>
        </div>
      </ReactFlowProvider>
    );
  }

  // Show editor
  return (
    <ReactFlowProvider>
      <div className="workflow-editor-container">
        {/* Top Toolbar - no onSave prop needed */}
        <TopToolbar />

        {/* Main Content */}
        <div className="workflow-editor-main">
          {/* Left Toolbar - only show in design mode */}
          {mode === 'design' && <LeftToolbar />}

          {/* Canvas */}
          <div className="workflow-editor-canvas-container">
            <WorkflowCanvas />

            {/* Properties panels — rendered outside react-flow-wrapper to ensure correct z-index */}
            {selectedHandle && <HandleProperties />}
            {selectedEdges.length === 1 && !selectedHandle && <EdgeProperties />}

            {/* Panel toggle buttons */}
            {mode === 'design' && (
              <div className="workflow-panel-overlay">
                <button
                  className={`workflow-collapse-tab workflow-collapse-tab--left${leftPanelOpen ? ' is-open' : ''}`}
                  onClick={toggleLeftPanel}
                  title={leftPanelOpen ? 'Collapse panel' : 'Expand panel'}
                >
                  {leftPanelOpen ? <ArrowLeftIcon size={12} /> : <ChevronRightIcon size={12} />}
                </button>

                <button
                  className={`workflow-collapse-tab workflow-collapse-tab--right${rightPanelOpen ? ' is-open' : ''}`}
                  onClick={toggleRightPanel}
                  title={rightPanelOpen ? 'Collapse properties' : 'Expand properties'}
                >
                  {rightPanelOpen ? <ChevronRightIcon size={12} /> : <ArrowLeftIcon size={12} />}
                </button>
              </div>
            )}
          </div>

          {/* Right Panel - always visible in all modes (Properties) */}
          <RightToolbar />
        </div>

        {/* FlowObserver bottom panel — docked below main content. Its "HIL Actions" tab
            (HilObservePanel) is the sole HIL rendering surface now — see comment above. */}
        <FlowObserverPanel theme={theme} />
      </div>
    </ReactFlowProvider>
  );
};

export default WorkflowEditor;
